import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";

// English is the fallback for every missing key, so it is the one dictionary that
// must always be present — it ships statically. The other seven are fetched only
// for the locale actually being viewed: bundling all eight put ~158 kB of
// translations in front of every visitor to read one of them.
import en from "@/locales/en.json";

export type Locale = "en" | "es" | "fr" | "de" | "it" | "pt" | "zh" | "ru";

export const LOCALES: Locale[] = ["en", "es", "fr", "de", "it", "pt", "zh", "ru"];

type Dictionary = Record<string, string>;

// Static object keys, so Vite can statically analyse these and emit one chunk each.
const LOADERS: Record<Exclude<Locale, "en">, () => Promise<{ default: Dictionary }>> = {
  es: () => import("@/locales/es.json"),
  fr: () => import("@/locales/fr.json"),
  de: () => import("@/locales/de.json"),
  it: () => import("@/locales/it.json"),
  pt: () => import("@/locales/pt.json"),
  zh: () => import("@/locales/zh.json"),
  ru: () => import("@/locales/ru.json"),
};

const loaded: Partial<Record<Locale, Dictionary>> = { en: en as Dictionary };

export function isLocale(value: string): value is Locale {
  return (LOCALES as string[]).includes(value);
}

/**
 * The first of the browser's preferred languages we actually speak, or `null`.
 *
 * Tags arrive as BCP-47 ("es-419", "pt-BR", "zh-Hans-CN"), so only the primary subtag
 * is compared: a Mexican visitor gets Spanish rather than falling through to English
 * because "es-MX" is not one of the eight dictionary names. Anything we do not have a
 * dictionary for is skipped, and English remains the fallback when nothing matches.
 */
export function detectBrowserLocale(): Locale | null {
  if (typeof navigator === "undefined") return null;
  const preferred = navigator.languages?.length
    ? navigator.languages
    : [navigator.language].filter(Boolean);
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split("-")[0];
    if (isLocale(primary)) return primary;
  }
  return null;
}

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathLang = pathname.split("/")[1];
  const isValidLang = isLocale(pathLang);

  const [locale, setLocaleState] = useState<Locale>(isValidLang ? pathLang : "en");
  // Bumped once a dictionary finishes loading, to re-render with the real strings.
  const [, setDictionaryVersion] = useState(0);

  useEffect(() => {
    // Order of authority: the URL, then the language the user picked in the switcher,
    // then the browser. Detection runs in an effect rather than during render because
    // the server has no `navigator` — choosing the locale before hydration would make
    // the client render something the SSR HTML does not contain.
    //
    // A `$lang` URL decides the language of that page but is *not* stored as a
    // preference: `/blog` redirects to `/en/blog` and search results land on English
    // pages, so persisting it would silently pin a Spanish-speaking visitor to English
    // for every screen afterwards. Only `setLocale` writes the preference.
    const stored = (typeof window !== "undefined" &&
      localStorage.getItem("locale")) as Locale | null;
    if (isValidLang) {
      setLocaleState(pathLang);
    } else if (stored && isLocale(stored)) {
      setLocaleState(stored);
    } else {
      // Nothing chosen yet: follow the browser, English if we don't speak its language.
      setLocaleState(detectBrowserLocale() ?? "en");
    }
  }, [pathname, isValidLang, pathLang]);

  useEffect(() => {
    if (locale === "en" || loaded[locale]) return;
    let cancelled = false;
    LOADERS[locale as Exclude<Locale, "en">]()
      .then((module) => {
        if (cancelled) return;
        loaded[locale] = module.default;
        setDictionaryVersion((v) => v + 1);
      })
      // Falling back to English is a worse experience than the right language, but a
      // far better one than a blank screen.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    if (typeof window !== "undefined") localStorage.setItem("locale", l);
  };

  const t = (key: string, vars?: Record<string, string | number>) => {
    let str = loaded[locale]?.[key] ?? (en as Dictionary)[key] ?? key;
    if (vars) for (const k in vars) str = str.replaceAll(`{${k}}`, String(vars[k]));
    return str;
  };

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n outside provider");
  return ctx;
}
